// import { useState, useEffect } from 'react'
import { useQuery } from 'react-query'
import { useMemo, useState } from 'react'

// Libs
import { fetchSearchData, fetchSearchTagData } from '@/api/fetchSearchData'

// Constants
import { QUERY_KEY } from '@/constants/queryKey'
import { MALL_DEFAULT_PAGE_SIZE } from '@/constants/mall'

// Types
interface SearchProps {
  keyword?: string
  isProductPagination?: boolean
  productSort?: (data: ISearchProductInfo[]) => ISearchProductInfo[]
  isTag?: boolean
}
export interface ISearchProductInfo {
  categoryId: number
  categoryName: string
  currency: string
  dummySalesVolume: number
  images: string[]
  logistics: string[]
  maxLivePrice: number
  maxOrigPrice: number
  maxPrice: number
  merchantId: number
  merchantName: string
  minLivePrice: number
  minOrigPrice: number
  minPrice: number
  offShelfDate: string
  onShelfDate: string
  origPrice: number
  productDesc: string
  productDetail: string
  productId: number
  productName: string
  saleQuantity: number
  shippingDays: number
  status: number
}

export interface ISearchData {
  merchant: {
    merchantId: number
    storeName: string
    headshotUrl: string
    memberInfoId: string
    refCode: string
    contactPerson: string
    contactNumber: string
    phone: string
    email: string
    totalProduct: number
    intro: string
  }[]
  kol: {
    accumulateViews: number
    appendix: string
    followerNum: number
    headshotUrl: string
    intro: string
    kolCoverPhoto: string
    memberInfoId: string
    nickName: string
    refCode: string
  }[]
  product: ISearchProductInfo[]
  room: {
    title: string
    coverPhotoUrl: string
    startTime: string
    endTime: string
    roomId: string
    sessionRoomId: string
    tickerTape: string[]
    roomDesc: string
    roomPlacard: string
    dummyViews: number
    kolName: string
    clientSn: number
    liveType: number
    memberInfoId: string
    kolHeadshotUrl: string
    kolFirstEntry: string
    likes: number
    isRecord: boolean
    liveTime: string
    liveCloseTime: string
  }[]
  roomKol: {
    accumulateViews: number
    appendix: string
    followerNum: number
    headshotUrl: string
    intro: string
    kolCoverPhoto: string
    memberInfoId: string
    nickName: string
    refCode: string
  }[]
}
interface Result {
  searchData: ISearchData
  isSearchDataFetched: boolean
  isSearchDataSuccess: boolean
  productPaginationData: { data: { pages: { productsInfo: [] }[] } }
}
const splitDataToPagination = (data: any, pageSize: number): { productsInfo: [] }[] => {
  const result = []
  for (let i = 0; i < data.length; i += pageSize) {
    result.push({ productsInfo: data.slice(i, i + pageSize) })
  }
  return result
}
const useSearchData = ({ keyword = '', productSort = () => [], isProductPagination = false, isTag = false }: SearchProps): Result => {
  const {
    data: searchData = { merchant: [], kol: [], product: [], room: [], roomKol: [] } as ISearchData,
    isFetched: isSearchDataFetched,
    isSuccess: isSearchDataSuccess,
  } = useQuery([QUERY_KEY.FETCH_SEARCH_DATA, { keyword }], () => isTag ? fetchSearchTagData({ keyword:keyword }) : fetchSearchData({ keyword }), {})
  const [currentPage, setCurrentPage] = useState(1)

  const productDataPages = useMemo(() => {
    let paginationProductData: any[] = [{ productsInfo: null }]
    if (isProductPagination) {
      const sortedProductData = searchData?.product.length > 0 ? productSort(searchData?.product) : [null]
      if (sortedProductData[0]) {
        paginationProductData = splitDataToPagination(sortedProductData, MALL_DEFAULT_PAGE_SIZE)
      }
    }

    return paginationProductData
  }, [searchData, productSort])
  const productPaginationData = {
    data: {
      pages: [...productDataPages].slice(0, currentPage),
    },
    fetchNextPage: () => {
      setCurrentPage(currentPage + 1)
    },
    hasNextPage: currentPage < productDataPages.length ? true : false,
    isFetched: isSearchDataFetched,
    isSuccess: isSearchDataSuccess,
  }
  return {
    searchData,
    isSearchDataFetched,
    isSearchDataSuccess,
    productPaginationData,
  }
}
export default useSearchData
